import { useEffect, useState } from "react";
import { DataGridPro as DataGrid } from "@mui/x-data-grid-pro";
import PlaceIcon from "@mui/icons-material/Place";
import DomainIcon from "@mui/icons-material/Domain";
import SchoolIcon from "@mui/icons-material/School";
import PersonIcon from "@mui/icons-material/Person";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import { useNavigate } from "react-router-dom";
import Translate from "../../utils/Translate";
import { dataGridLang } from "../MemToolBar";
import DotMenu from "../DotMenu";
import { CustomNoRowsOverlay } from "../../utils/StyledGridOverlay";
import { IconAndLabelColumnHeader, TableAvatar2 } from "../../utils/dataGrid";
import { Grid, Stack, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { Error } from "@mui/icons-material";

const defaultPaginationModel = {
  pageSize: 25,
  page: 0,
};

function savePaginationModel(paginationSessionStorageKey, paginationModel) {
  if (paginationSessionStorageKey) {
    sessionStorage.setItem(paginationSessionStorageKey, JSON.stringify(paginationModel));
  }
}

function loadPaginationModel(paginationSessionStorageKey) {
  return paginationSessionStorageKey && sessionStorage[paginationSessionStorageKey]
    ? JSON.parse(sessionStorage[paginationSessionStorageKey])
    : null;
}

const Comp = ({
  rows,
  personConnectionTypesByCompanyId,
  smallScreen,
  getDotMenuActions,
  noRowsTransKey,
  isPersonCard,
  onNavigatedToActor,
  paginationSessionStorageKey,
  restorePaginationSession,
}) => {
  const [paginationModel, setPaginationModel] = useState(
    restorePaginationSession && paginationSessionStorageKey
      ? (loadPaginationModel(paginationSessionStorageKey) ?? defaultPaginationModel)
      : defaultPaginationModel,
  );
  const navigate = useNavigate();
  const localizedTextsMap = dataGridLang();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  let columns = [
    {
      field: "actorName",
      headerName: Translate.get("Companies"),
      flex: 1,
      renderHeader: () => <IconAndLabelColumnHeader icon={DomainIcon} label={Translate.get("Companies")} />,
      renderCell: (params) => {
        return (
          <div style={{ marginTop: "1em" }}>
            <TableAvatar2
              image={params.row.profilePictureUrl}
              name={params.row.actorName}
              externalActorNo={params.row.externalDataId}
              observationNote={params.row.observationNote}
            ></TableAvatar2>
          </div>
        );
      },
    },
    {
      field: "custom2",
      headerName: Translate.get("City"),
      renderHeader: () => <IconAndLabelColumnHeader icon={PlaceIcon} label={Translate.get("City")} />,
      renderCell: (params) => {
        return (
          <Stack direction="row" gap={1.1}>
            {params.row.postalAddress?.actorAddressZipCode && <div>{params.row.postalAddress.actorAddressZipCode}</div>}
            {params.row.postalAddress?.actorAddressCity && <div>{params.row.postalAddress.actorAddressCity}</div>}
          </Stack>
        );
      },
      valueGetter: (params, row, column, apiRef) => row?.postalAddress?.actorAddressCity || "",
      sortComparator: (v1, v2) => v1.localeCompare(v2),
      flex: 1,
    },
    {
      field: "custom1",
      headerName: Translate.get("Address"),
      renderHeader: () => <IconAndLabelColumnHeader icon={PlaceIcon} label={Translate.get("Address")} />,
      renderCell: (params) => {
        return (
          <div>
            <div>{params.row.postalAddress?.actorAddressStreet2}</div>
          </div>
        );
      },
      valueGetter: (value, row, column, apiRef) => row?.postalAddress?.actorAddressStreet1 || "",
      sortComparator: (v1, v2) => v1.localeCompare(v2),
      flex: 1,
    },
    {
      field: "custom",
      headerName: Translate.get("Role"),
      renderHeader: () => <IconAndLabelColumnHeader icon={AssignmentIndIcon} label={Translate.get("Role")} />,
      filterable: false,
      display: "flex",
      renderCell: (params) =>
        personConnectionTypesByCompanyId && (
          <div className="personRoles">
            {personConnectionTypesByCompanyId[params.row.actorId].isContactConnection && (
              <Tooltip title={Translate.getActorType(3)}>
                <HeadsetMicIcon />
              </Tooltip>
            )}
            {personConnectionTypesByCompanyId[params.row.actorId].isStudentConnection && (
              <Tooltip title={Translate.getActorType(4)}>
                <PersonIcon />
              </Tooltip>
            )}
          </div>
        ),
    },
    {
      field: "dotMenu",
      flex: 0.3,
      headerName: "",
      sortable: false,
      disableColumnMenu: true,
      filterable: false,
      headerClassName: "hideSeparator",
      align: "right",
      hide: !getDotMenuActions,
      hideable: false,
      renderCell: (params) =>
        getDotMenuActions && (
          <div>
            <DotMenu content={getDotMenuActions(params.row.actorId)} horizontal />
          </div>
        ),
    },
  ];

  function handlePaginationChange(model) {
    savePaginationModel(paginationSessionStorageKey, model);
    setPaginationModel(model);
  }

  const navToCompany = (id) => {
    if (onNavigatedToActor) {
      onNavigatedToActor();
    }
    navigate(`/dashboard/company/${id}`, { replace: false });
  };

  // For small screens
  const listViewColDef = {
    field: "listColumn",
    display: "flex",
    renderCell: (params) => {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            marginTop: "1em",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ flex: 1 }}>
              <TableAvatar2
                image={params.row.profilePictureUrl}
                name={params.row.actorName}
                city={`${params.row.postalAddress?.actorAddressZipCode || ""} ${
                  params.row.postalAddress?.actorAddressCity || ""
                }`}
                observationNote={params.row.observationNote}
              />
            </div>
            <div> {getDotMenuActions && <DotMenu content={getDotMenuActions(params.row.actorId)} horizontal />}</div>
          </div>
          <div style={{ marginTop: "0.5em", marginLeft: "3.3em" }}>
            {personConnectionTypesByCompanyId && (
              <div className="personRoles">
                {personConnectionTypesByCompanyId[params.row.actorId].isContactConnection && (
                  <Tooltip title={Translate.getActorType(3)}>
                    <HeadsetMicIcon />
                  </Tooltip>
                )}
                {personConnectionTypesByCompanyId[params.row.actorId].isStudentConnection && (
                  <Tooltip title={Translate.getActorType(4)}>
                    <PersonIcon />
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        </div>
      );
    },
  };

  return (
    <>
      <DataGrid
        initialState={{
          sorting: {
            sortModel: [{ field: "actorName", sort: "asc" }],
          },
        }}
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        getRowId={(row) => row.actorId}
        rowHeight={isSmallScreen && isPersonCard ? 80 : 50}
        onRowClick={(params) => navToCompany(params.id)}
        localeText={localizedTextsMap}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay(Translate.get("EmptyHereRegister")),
        }}
        slotProps={{
          toolbar: { gridId: "Companies" },
          columnsPanel: { className: "customColumnPanel" },
          filterPanel: { className: "customfilterPanel" },
          panel: { className: "customPanel" },
        }}
        columnVisibilityModel={{
          custom: isPersonCard ?? false,
          custom2: !isPersonCard,
          custom1: !isPersonCard,
        }}
        disableColumnMenu
        disableDensitySelector={true}
        density="comfortable"
        pagination
        pageSizeOptions={[25, 100, 250]}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        listView={isSmallScreen}
        listViewColumn={isSmallScreen ? listViewColDef : undefined}
      />
    </>
  );
};
export default Comp;
